// Template gallery (docs/09 §B1) — the entry point of the "Create" flow. Shows the
// first-party templates as cards, each with a lazily-rendered mini preview (the
// template's defaults, rendered once the card scrolls into view). Picking a card
// hands off to the instantiation form.

import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal.tsx';
import { ScaledFrame } from '../../components/ScaledFrame.tsx';
import { Loading, ErrorState } from '../../components/ui.tsx';
import {
  useTemplates,
  renderTemplatePreview,
  defaultFields,
  type Template,
} from '../../api/templates.ts';

/** Renders the template's default preview once the card is on screen (lazy). */
function MiniPreview({ template }: { template: Template }) {
  const [html, setHtml] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '100px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const controller = new AbortController();
    renderTemplatePreview(template.id, defaultFields(template), controller.signal)
      .then(setHtml)
      .catch(() => {
        /* mini preview is best-effort; the icon-less frame just stays blank */
      });
    return () => controller.abort();
  }, [visible, template]);

  return (
    <div className="tpl-card-preview" ref={ref}>
      {html != null && <ScaledFrame title={`${template.name} preview`} srcDoc={html} />}
    </div>
  );
}

export function TemplateGalleryModal({
  onPick,
  onClose,
}: {
  onPick: (templateId: string) => void;
  onClose: () => void;
}) {
  const templates = useTemplates();

  return (
    <Modal title="Choose a template" onClose={onClose} size="lg">
      {templates.isLoading ? (
        <Loading />
      ) : templates.isError ? (
        <ErrorState message="Could not load templates." onRetry={() => templates.refetch()} />
      ) : (
        <div className="tpl-gallery">
          {templates.data!.map((t) => (
            <button
              key={t.id}
              type="button"
              className="tpl-card"
              onClick={() => onPick(t.id)}
              aria-label={`Use the ${t.name} template`}
            >
              <MiniPreview template={t} />
              <div className="tpl-card-body">
                <div className="tpl-card-name">{t.name}</div>
                <div className="tpl-card-desc">{t.description}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
